import { useState, useEffect, useRef } from "react";
import LanguageSwitcher from "@/components/base/LanguageSwitcher";
import { useCart } from "@/hooks/useCart";
import { useAuth } from "@/hooks/useAuth";
import UserDropdown from "@/components/feature/UserDropdown";
import { Link, useLocation } from "react-router-dom";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const isRtl = i18n.language?.startsWith("ar");
  const { itemCount } = useCart();
  const { isAuthenticated } = useAuth();

  // Only the homepage has a dark hero; inner pages should always show the solid navbar
  const isHomePage = location.pathname === "/";
  const showSolid = scrolled || !isHomePage;

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 20);
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLinks = [
    { href: "/", label: t("nav.home") },
    { href: "/products", label: t("nav.products") },
    { href: "/products", label: t("nav.deals") },
    { href: "/admin", label: t("nav.admin") },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showSolid
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link
            to="/"
            className="flex-shrink-0 cursor-pointer"
          >
            <h1 className={`text-xl md:text-2xl font-bold whitespace-nowrap font-heading tracking-tight ${
              showSolid ? "text-foreground-950" : "text-white"
            }`}>
              Galore
            </h1>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  showSolid
                    ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-200/70"
                    : "text-white/90 hover:text-white hover:bg-white/10"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-1">
            {/* Search */}
            <div ref={searchRef} className="relative">
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className={`p-2 rounded-lg transition-colors cursor-pointer ${
                  showSolid
                    ? "text-foreground-600 hover:text-foreground-950 hover:bg-background-200/70"
                    : "text-white/90 hover:text-white hover:bg-white/10"
                }`}
                aria-label="Search"
              >
                <i className="ri-search-line text-lg"></i>
              </button>
              {searchOpen && (
                <div className={`absolute top-full mt-2 ${isRtl ? "left-0" : "right-0"} w-72 bg-background-50 border border-background-200 rounded-xl p-3 z-50`}>
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                    <input
                      type="text"
                      placeholder={t("nav.search_placeholder")}
                      className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      autoFocus
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Wishlist */}
            <Link
              to="/account/wishlist"
              className={`p-2 rounded-lg transition-colors cursor-pointer ${
                showSolid
                  ? "text-foreground-600 hover:text-foreground-950 hover:bg-background-200/70"
                  : "text-white/90 hover:text-white hover:bg-white/10"
              }`}
              aria-label="Wishlist"
            >
              <i className="ri-heart-line text-lg"></i>
            </Link>

            {/* Cart */}
            <Link
              to="/cart"
              className={`relative p-2 rounded-lg transition-colors cursor-pointer ${
                showSolid
                  ? "text-foreground-600 hover:text-foreground-950 hover:bg-background-200/70"
                  : "text-white/90 hover:text-white hover:bg-white/10"
              }`}
              aria-label="Cart"
            >
              <i className="ri-shopping-bag-line text-lg"></i>
              {itemCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-primary-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>

            {/* Account */}
            {isAuthenticated ? (
              <UserDropdown scrolled={showSolid} />
            ) : (
              <Link
                to="/login"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  showSolid
                    ? "bg-primary-500 text-white hover:bg-primary-600"
                    : "bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 border border-white/20"
                }`}
              >
                {t("nav.login")}
              </Link>
            )}

            {/* Language Switcher */}
            <div className={showSolid ? "" : "text-white/90"}>
              <LanguageSwitcher />
            </div>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden p-2 rounded-lg transition-colors cursor-pointer ${
                showSolid
                  ? "text-foreground-600 hover:text-foreground-950 hover:bg-background-200/70"
                  : "text-white/90 hover:text-white hover:bg-white/10"
              }`}
              aria-label="Menu"
            >
              {mobileOpen ? (
                <i className="ri-close-line text-xl"></i>
              ) : (
                <i className="ri-menu-line text-xl"></i>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200 py-4 px-2 animate-in slide-in-from-top-2">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  {link.label}
                </Link>
              ))}
              <div className="border-t border-background-200 my-2"></div>
              {isAuthenticated ? (
                <>
                  <Link
                    to="/account"
                    onClick={() => setMobileOpen(false)}
                    className="px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-3"
                  >
                    <i className="ri-user-line"></i>
                    {t("account.my_account")}
                  </Link>
                  <Link
                    to="/account/orders"
                    onClick={() => setMobileOpen(false)}
                    className="px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-3"
                  >
                    <i className="ri-shopping-bag-line"></i>
                    {t("account.orders")}
                  </Link>
                  <Link
                    to="/account/wishlist"
                    onClick={() => setMobileOpen(false)}
                    className="px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-3"
                  >
                    <i className="ri-heart-line"></i>
                    {t("account.wishlist")}
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMobileOpen(false)}
                    className="px-4 py-3 rounded-lg text-sm font-medium text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    {t("nav.login")}
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setMobileOpen(false)}
                    className="px-4 py-3 rounded-lg text-sm font-medium bg-primary-500 text-white text-center hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    {t("nav.register")}
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}